import { FC, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import {
  Box, Grid, Table, TableBody, TableHead, TableRow, TableCell, tableCellClasses, Chip, Button, CircularProgress,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchUnits } from "../../store/unitsStore/unitsThunk";
import { useInfiniteReport } from "../../hooks/useInfiniteReport";
import ReusableReportHeader from "../Reports/ReusableReportHeader";
import LocationReportFilter from "./pickers/LocationReportFilter";
import { FilterSelect } from "./InvFilters";
import Filters from "../Filters/Filters";

// Stock-transfer report — one row per line item (header rowSpans its SKU lines), server-paginated + server-side filters.

const STATUS_COLOR: Record<string, string> = { draft: "#b08900", dispatched: "#488dff", received: "#49a563", cancelled: "#c33b3b" };
const TRANSFER_STATUSES = ["draft", "dispatched", "received", "cancelled"];
const statusLabel = (s: string) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#eaecf0", color: "#000", border: "0.75px solid #d9d4d4",
    padding: 5, fontSize: "9pt", paddingLeft: "12px", fontWeight: 600, whiteSpace: "nowrap",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "9pt", fontWeight: "normal", color: "#292d35",
    padding: 5, border: "0.75px solid #d9d4d4", paddingLeft: "12px", verticalAlign: "top",
  },
}));

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");
const itemQty = (i: any) => Number(i?.qty ?? i?.quantity ?? 0);
const money = (n: any) => `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
const esc = (s: any) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

// Header (transfer-level, spanned) + item-level columns.
const HEADER_COLS = ["Date", "Transfer #", "From", "To", "Status", "Created By"];
const ITEM_COLS = ["Item", "Item Code", "Batch", "Expiry", "Qty", "Unit" /*, "Unit Cost" */];
const ALL_COLS = [...HEADER_COLS, ...ITEM_COLS];

const TransferReport: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const unitsById = useAppSelector((state) => state.units.byId);
  const unitsList = useAppSelector((state) => state.units.units);

  // Item `unit` is stored as a MeasuringUnit id — resolve to a readable code/name.
  const unitText = (u?: string) => (u ? unitsById?.[u]?.code || unitsById?.[u]?.name || u : "-");

  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Bumped on reset to remount the shared date-range dropdown back to "Select Date".
  const [filtersKey, setFiltersKey] = useState(0);

  useEffect(() => {
    if (!unitsList || unitsList.length === 0) dispatch(fetchUnits());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side, scroll-paginated data (50 records/page).
  const { rows, total, loading, loadingMore, hasMore, loadMore, collectAll } = useInfiniteReport(
    "/api/v1/erp/stock-transfers",
    { fromLocationId, toLocationId, status: statusFilter, startDate, endDate }
  );

  // Shared Filters dropdown emits "start=YYYY-MM-DD&end=YYYY-MM-DD" (empty for "Select Date").
  const handleDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    unstable_batchedUpdates(() => {
      setStartDate(params.get("start") || "");
      setEndDate(params.get("end") || "");
    });
  };

  const resetFilters = () => {
    setFromLocationId(""); setToLocationId(""); setStatusFilter(""); setStartDate(""); setEndDate("");
    setFiltersKey((k) => k + 1);
  };

  const onScroll = (e: any) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 300 && hasMore && !loadingMore && !loading) loadMore();
  };

  const rangeText = startDate || endDate ? `${startDate ? fmtDate(startDate) : "…"} – ${endDate ? fmtDate(endDate) : "…"}` : "All dates";

  // -------- export (pages through the FULL filtered set, not just what's loaded) --------
  const flatRows = (data: any[]): string[][] =>
    data.flatMap((t: any) => {
      const items = (t.items && t.items.length) ? t.items : [null];
      return items.map((it: any): string[] => [
        fmtDate(t.createdAt), t.transferNumber || "", t.fromLocationId?.name || "", t.toLocationId?.name || "",
        statusLabel(t.status || ""), t.createdBy?.name || "",
        it?.skuId?.name || "", it?.skuId?.itemCode || "", it?.batchId?.batchNumber || "",
        it?.batchId?.expiryDate ? fmtDate(it.batchId.expiryDate) : "",
        it ? String(itemQty(it)) : "", it ? unitText(it.unit) : "",
        // it ? String(Number(it.unitCost) || 0) : "",
      ]);
    });

  const buildTableHtml = (data: any[]) => `
    <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;font-family:Arial;font-size:11px">
      <thead><tr style="background:#eaecf0">${ALL_COLS.map((c) => `<th align="left">${esc(c)}</th>`).join("")}</tr></thead>
      <tbody>${flatRows(data).map((r) => `<tr>${r.map((v) => `<td>${esc(v)}</td>`).join("")}</tr>`).join("")}</tbody>
    </table>`;

  const handleDownloadExcel = async () => {
    const all = await collectAll();
    const html = `<html><head><meta charset="utf-8" /></head><body>${buildTableHtml(all)}</body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Stock_Transfer_Report_${new Date().toISOString().split("T")[0]}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = async () => {
    const all = await collectAll();
    const lines = all.reduce((s: number, t: any) => s + Math.max((t.items || []).length, 1), 0);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Stock Transfer Report</title>
      <style>body{font-family:Arial;padding:16px} h3{margin:0 0 4px} .sub{color:#666;font-size:12px;margin-bottom:12px}
      table{border-collapse:collapse;width:100%;font-size:11px} th,td{border:0.75px solid #d9d4d4;padding:5px;text-align:left}
      thead tr{background:#eaecf0}</style></head>
      <body><h3>Stock Transfer Report</h3><div class="sub">${esc(rangeText)} · ${all.length} transfers · ${lines} lines</div>
      ${buildTableHtml(all)}</body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:absolute;width:0;height:0;border:0;left:-9999px;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) return;
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus(); iframe.contentWindow?.print();
        setTimeout(() => document.body.removeChild(iframe), 500);
      }, 300);
    };
  };

  // Dashboard report-card's download button opens this page with ?download=excel|pdf —
  // trigger that export once and clean the URL.
  useEffect(() => {
    const dl = new URLSearchParams(window.location.search).get("download");
    if (!dl) return;
    window.history.replaceState({}, "", window.location.pathname);
    const t = setTimeout(() => { dl === "pdf" ? handleDownloadPDF() : handleDownloadExcel(); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ width: "100%", pr: 1 }}>
      <Box sx={{ width: "100%", overflow: "hidden", mb: 2 }} className={"c-card2"}>
        <ReusableReportHeader
          reportType="stock_transfer"
          title="Stock Transfer Report"
          subtitle={`${rangeText} · ${total} transfers`}
          recordCount={total}
          isLoading={loading}
          backTo="/inventory/transfers"
          showPrint={false}
          showShare={false}
          onDownloadPDF={handleDownloadPDF}
          onDownloadExcel={handleDownloadExcel}
        />

        {/* Filters strip */}
        <Grid container spacing={1.5} alignItems="center" p={2} style={{ borderBottom: ".5px solid #9da7b7" }}>
          <Grid item>
            <LocationReportFilter borderless width={170} value={fromLocationId} onChange={(v: string) => setFromLocationId(v)} label="From" activeOnly={false} />
          </Grid>
          <Grid item>
            <LocationReportFilter borderless width={170} value={toLocationId} onChange={(v: string) => setToLocationId(v)} label="To" activeOnly={false} />
          </Grid>
          <Grid item>
            <FilterSelect
              value={statusFilter}
              onChange={(v: string) => setStatusFilter(v)}
              placeholder="Status"
              width={150}
              options={TRANSFER_STATUSES.map((s) => ({ label: statusLabel(s), value: s }))}
            />
          </Grid>
          <Grid item>
            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
              <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Date</span>
              <Filters key={filtersKey} type="inventory-transfer" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleDateFilter} />
            </div>
          </Grid>
          <Grid item>
            <Button size="small" color="inherit" startIcon={<RestartAltIcon />} onClick={resetFilters} sx={{ color: "#7a8290" }}>
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Report table — one transfer header spans its line items; scroll loads more */}
        <div onScroll={onScroll} style={{ maxHeight: "62vh", overflow: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 1100 }} size="small" aria-label="stock transfer report">
            <TableHead>
              <TableRow>
                {ALL_COLS.map((c) => <StyledTableCell key={c}>{c}</StyledTableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? rows.map((t: any) => {
                const items = (t.items && t.items.length) ? t.items : [null];
                const span = items.length;
                return items.map((it: any, idx: number) => (
                  <TableRow key={`${t._id}-${idx}`}>
                    {idx === 0 && (
                      <>
                        <StyledTableCell rowSpan={span}>{fmtDate(t.createdAt)}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>
                          <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => navigate(`/inventory/transfers/${t._id}`)}>
                            {t.transferNumber}
                          </span>
                        </StyledTableCell>
                        <StyledTableCell rowSpan={span}>{t.fromLocationId?.name || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>{t.toLocationId?.name || "-"}</StyledTableCell>
                        <StyledTableCell rowSpan={span}>
                          <Chip size="small" label={statusLabel(t.status || "")} sx={{ color: STATUS_COLOR[t.status] || "#555" }} />
                        </StyledTableCell>
                        <StyledTableCell rowSpan={span}>{t.createdBy?.name || "-"}</StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>{it?.skuId?.name || "-"}</StyledTableCell>
                    <StyledTableCell>{it?.skuId?.itemCode || "-"}</StyledTableCell>
                    <StyledTableCell>{it?.batchId?.batchNumber || "-"}</StyledTableCell>
                    <StyledTableCell>{it?.batchId?.expiryDate ? fmtDate(it.batchId.expiryDate) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? itemQty(it) : "-"}</StyledTableCell>
                    <StyledTableCell>{it ? unitText(it.unit) : "-"}</StyledTableCell>
                    {/* <StyledTableCell>{it ? money(it.unitCost) : "-"}</StyledTableCell> */}
                  </TableRow>
                ));
              }) : (
                <TableRow>
                  <StyledTableCell colSpan={ALL_COLS.length} style={{ textAlign: "center", color: "#9da7b7", padding: 24 }}>
                    {loading ? "Loading…" : "No transfers match the selected filters."}
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {loadingMore ? (
            <div style={{ textAlign: "center", padding: 12 }}><CircularProgress size={20} /></div>
          ) : (!hasMore && rows.length > 0) ? (
            <div style={{ textAlign: "center", padding: 10, color: "#c7ccd4", fontSize: 12 }}>All {total} transfers loaded</div>
          ) : null}
        </div>
      </Box>
    </Box>
  );
};

export default TransferReport;
